// src/components/Quote/QuoteLogisticsTab.jsx
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import AddressAutocomplete from "./AddressAutocomplete";

/* ─── Champs ────────────────────────────────────────────────── */
const leftFields = [
  { key: "loading_floor",         tkey: "floor",         type: "number" },
  { key: "loading_lift",          tkey: "lift",          type: "toggle" },
  { key: "loading_piano",         tkey: "piano",         type: "toggle" },
  { key: "loading_portage",       tkey: "carrying",      type: "toggle" },
  { key: "loading_monte_meubles", tkey: "furnitureLift", type: "toggle" },
  { key: "loading_window_pass",   tkey: "windowPass",    type: "toggle" },
  { key: "loading_garage",        tkey: "garage",        type: "toggle" },
  { key: "loading_cave",          tkey: "cellar",        type: "toggle" },
  { key: "margin",                tkey: "safetyMargin",  type: "number" },
];

const rightFields = [
  { key: "delivery_floor",         tkey: "floor",         type: "number" },
  { key: "delivery_lift",          tkey: "lift",          type: "toggle" },
  { key: "delivery_piano",         tkey: "piano",         type: "toggle" },
  { key: "delivery_portage",       tkey: "carrying",      type: "toggle" },
  { key: "delivery_monte_meubles", tkey: "furnitureLift", type: "toggle" },
  { key: "delivery_window_pass",   tkey: "windowPass",    type: "toggle" },
  { key: "delivery_garage",        tkey: "garage",        type: "toggle" },
  { key: "delivery_cave",          tkey: "cellar",        type: "toggle" },
];

/* ─── Defaults ──────────────────────────────────────────────── */
const defaultFields = {
  loading_date: "", loading_address: "", loading_city: "", loading_zip: "",
  delivery_date: "", delivery_address: "", delivery_city: "", delivery_zip: "",
  loading_lat: null, loading_lng: null,
  delivery_lat: null, delivery_lng: null,
  distance: "",
  volume: 0, __manualVolume: false,
  comments: "",
  service_emballage: false, service_deballage: false,
  service_montage: false,   service_demontage: false,
};
[...leftFields, ...rightFields].forEach((f) => {
  if (!(f.key in defaultFields)) defaultFields[f.key] = "";
});

/* ─── Sub-components ─────────────────────────────────────────── */
const Label = ({ children }) => (
  <span className="text-xs font-semibold text-base-content/50 uppercase tracking-wider">{children}</span>
);

const Input = ({ type = "text", value, onChange, placeholder, required, className = "" }) => (
  <input
    type={type}
    value={value ?? ""}
    onChange={onChange}
    required={required}
    placeholder={placeholder}
    className={`w-full px-3 py-2.5 rounded-xl border border-base-300 bg-base-200 text-base-content text-sm
      placeholder:text-base-content/30 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400
      transition-all ${className}`}
  />
);

/* Oui / Non / — toggle */
const ToggleGroup = ({ value, onChange }) => (
  <div className="flex rounded-xl overflow-hidden border border-base-300 bg-base-200 text-sm font-medium">
    {[{ v: "", l: "—" }, { v: "Non", l: "Non" }, { v: "Oui", l: "Oui" }].map(({ v, l }) => (
      <button
        key={v}
        type="button"
        onClick={() => onChange(v)}
        className={`flex-1 py-2 transition-colors ${
          value === v
            ? "bg-blue-600 text-white shadow-sm"
            : "text-base-content/50 hover:text-base-content/70 hover:bg-base-200"
        }`}
      >
        {l}
      </button>
    ))}
  </div>
);

export default function QuoteLogisticsTab({ logistics, setLogistics, computedVolume }) {
  const { t } = useTranslation();
  const [calculating, setCalculating] = useState(false);
  const prevCoordsRef = useRef(null);

  const data = { ...defaultFields, ...(logistics || {}) };
  const set  = (field, value) => setLogistics({ ...data, [field]: value });

  /* ── Auto-calcul distance ── */
  useEffect(() => {
    const { loading_lat, loading_lng, delivery_lat, delivery_lng } = data;
    if (!loading_lat || !loading_lng || !delivery_lat || !delivery_lng) return;
    const key = `${loading_lat},${loading_lng}|${delivery_lat},${delivery_lng}`;
    if (prevCoordsRef.current === key) return;
    prevCoordsRef.current = key;

    setCalculating(true);
    fetch(
      `https://router.project-osrm.org/route/v1/driving/${loading_lng},${loading_lat};${delivery_lng},${delivery_lat}?overview=false`,
      { headers: { "User-Agent": "TheMovers/1.0" } }
    )
      .then((r) => r.json())
      .then((json) => {
        if (json.code === "Ok" && json.routes?.length)
          setLogistics({ ...data, distance: Math.round(json.routes[0].distance / 1000) });
      })
      .catch(() => {})
      .finally(() => setCalculating(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.loading_lat, data.loading_lng, data.delivery_lat, data.delivery_lng]);

  /* ── Volume ── */
  const isManual    = !!data.__manualVolume;
  const shownVolume = isManual
    ? Number(data.volume || 0)
    : Number((typeof computedVolume === "number" ? computedVolume : data.volume) || 0);

  /* ── Shared field renderer ── */
  const renderFields = (fields) =>
    fields.map(({ key, tkey, type }) => (
      <div key={key} className="flex flex-col gap-1.5">
        <Label>{t(`quote.${tkey}`)}</Label>
        {type === "toggle" ? (
          <ToggleGroup value={data[key]} onChange={(v) => set(key, v)} />
        ) : (
          <Input
            type={type}
            value={data[key]}
            onChange={(e) => set(key, e.target.value)}
            placeholder="0"
          />
        )}
      </div>
    ));

  /* ── Address renderer — inliné pour éviter unmount/remount au keystroke ── */
  const addressBlock = (prefix) => (
    <div className="flex flex-col gap-1.5">
      <Label>{t("quote.addressLabel")}</Label>
      <AddressAutocomplete
        value={data[`${prefix}_address`]}
        onChange={({ address, lat, lng, city, postcode }) =>
          setLogistics({
            ...data,
            [`${prefix}_address`]: address,
            [`${prefix}_lat`]: lat,
            [`${prefix}_lng`]: lng,
            ...(city ? { [`${prefix}_city`]: city } : {}),
            ...(postcode ? { [`${prefix}_zip`]: postcode } : {}),
          })
        }
        className="w-full px-3 py-2.5 rounded-xl border border-base-300 bg-base-200 text-base-content text-sm
          placeholder:text-base-content/30 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-all"
        placeholder={t("quote.addressTypePlaceholder")}
      />
      {data[`${prefix}_lat`] && (
        <span className="text-[11px] text-emerald-500 font-medium flex items-center gap-1">
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
          </svg>
          {t("quote.geolocated")}
        </span>
      )}
    </div>
  );

  return (
    <div className="space-y-4 p-1">

      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-4 px-1">
        <div>
          <h3 className="text-base font-bold text-base-content">
            {t("quote.logistics", "Logistique")}
          </h3>
          <p className="text-xs text-base-content/50 mt-0.5">
            {t("quote.logisticsHint", "Chargement & livraison — les champs * sont obligatoires.")}
          </p>
        </div>

        {/* Volume pill */}
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-2.5">
          <div className="text-right">
            <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider mb-0.5">{t("quote.volume")}</p>
            <div className="flex items-baseline gap-1">
              <input
                type="number"
                step="0.01"
                value={Number.isFinite(shownVolume) ? shownVolume : 0}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setLogistics({ ...data, volume: Number.isFinite(v) ? v : 0, __manualVolume: true });
                }}
                className="w-16 text-right text-blue-800 font-bold text-sm bg-transparent border-none outline-none focus:outline-none p-0"
              />
              <span className="text-blue-400 text-xs">m³</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setLogistics({
              ...data,
              volume: typeof computedVolume === "number" ? computedVolume : Number(data.volume || 0),
              __manualVolume: false,
            })}
            title={t("quote.autoVolumeTitle", "Revenir au volume automatique")}
            className={`text-[10px] font-semibold px-2 py-1 rounded-lg border transition-colors ${
              isManual
                ? "bg-base-100 text-blue-500 border-blue-200 hover:bg-blue-50"
                : "bg-blue-600 text-white border-blue-600"
            }`}
          >
            {t("quote.autoVolume")}
          </button>
        </div>
      </div>

      {/* ── Chargement / Livraison ── */}
      <div className="grid md:grid-cols-2 gap-4">

        {/* Chargement */}
        <div className="rounded-2xl overflow-hidden border border-base-300 shadow-sm">
          <div className="bg-blue-600 px-5 py-3.5 flex items-center gap-2">
            <span className="text-lg">📦</span>
            <h4 className="text-white font-semibold text-sm tracking-wide">
              {t("quote.loadingSection", "Chargement")}
            </h4>
          </div>
          <div className="bg-base-100 p-5 space-y-4">
            {/* Date */}
            <div className="flex flex-col gap-1.5">
              <Label>{t("quote.loadingDate")} <span className="text-red-400 normal-case">*</span></Label>
              <Input
                type="date"
                value={data.loading_date}
                onChange={(e) => set("loading_date", e.target.value)}
                required
              />
            </div>
            {/* Adresse */}
            {addressBlock("loading")}
            {/* Ville + Code postal */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 flex flex-col gap-1.5">
                <Label>{t("quote.city")}</Label>
                <Input
                  value={data.loading_city}
                  onChange={(e) => set("loading_city", e.target.value)}
                  placeholder="Paris"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t("quote.zip")}</Label>
                <Input
                  value={data.loading_zip}
                  onChange={(e) => set("loading_zip", e.target.value.replace(/\D/g, "").slice(0, 5))}
                  placeholder="75015"
                />
              </div>
            </div>
            {/* Séparateur */}
            <div className="border-t border-base-200 pt-3">
              <p className="text-[10px] font-semibold text-base-content/40 uppercase tracking-widest mb-3">
                {t("quote.equipmentAccess")}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {renderFields(leftFields)}
              </div>
            </div>
          </div>
        </div>

        {/* Livraison */}
        <div className="rounded-2xl overflow-hidden border border-base-300 shadow-sm">
          <div className="bg-emerald-600 px-5 py-3.5 flex items-center gap-2">
            <span className="text-lg">🚚</span>
            <h4 className="text-white font-semibold text-sm tracking-wide">
              {t("quote.deliverySection", "Livraison")}
            </h4>
          </div>
          <div className="bg-base-100 p-5 space-y-4">
            {/* Date */}
            <div className="flex flex-col gap-1.5">
              <Label>{t("quote.deliveryDate")} <span className="text-red-400 normal-case">*</span></Label>
              <Input
                type="date"
                value={data.delivery_date}
                onChange={(e) => set("delivery_date", e.target.value)}
                required
              />
            </div>
            {/* Adresse */}
            {addressBlock("delivery")}
            {/* Ville + Code postal (arrivée) */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 flex flex-col gap-1.5">
                <Label>{t("quote.city")}</Label>
                <Input
                  value={data.delivery_city}
                  onChange={(e) => set("delivery_city", e.target.value)}
                  placeholder="Lyon"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t("quote.zip")} <span className="text-red-400 normal-case">*</span></Label>
                <Input
                  value={data.delivery_zip}
                  onChange={(e) => set("delivery_zip", e.target.value.replace(/\D/g, "").slice(0, 5))}
                  placeholder="37000"
                />
              </div>
            </div>
            {/* Séparateur */}
            <div className="border-t border-base-200 pt-3">
              <p className="text-[10px] font-semibold text-base-content/40 uppercase tracking-widest mb-3">
                {t("quote.equipmentAccess")}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {renderFields(rightFields)}
              </div>
            </div>
            {/* Distance */}
            <div className="border-t border-base-200 pt-3">
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-semibold text-emerald-500 uppercase tracking-widest mb-0.5">
                    {t("quote.roadDistance")}
                  </p>
                  <div className="flex items-baseline gap-1.5">
                    <input
                      type="number"
                      value={data.distance}
                      onChange={(e) => set("distance", e.target.value)}
                      placeholder="—"
                      className="w-20 text-2xl font-bold text-emerald-700 bg-transparent border-none outline-none focus:outline-none p-0 placeholder:text-emerald-200"
                    />
                    <span className="text-emerald-500 font-semibold">km</span>
                  </div>
                </div>
                {calculating ? (
                  <span className="text-xs text-emerald-400 animate-pulse font-medium">{t("quote.distanceCalc")}</span>
                ) : data.loading_lat && data.delivery_lat && !data.distance ? (
                  <span className="text-xs text-emerald-300">{t("quote.distanceWaiting")}</span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Services complémentaires ── */}
      <div className="bg-base-100 rounded-2xl border border-base-300 shadow-sm overflow-hidden">
        <div className="bg-indigo-600 px-5 py-3.5 flex items-center gap-2">
          <span className="text-lg">🛠️</span>
          <h4 className="text-white font-semibold text-sm tracking-wide">
            {t("quote.additionalServices", "Services complémentaires")}
          </h4>
        </div>
        <div className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { key: "service_emballage", tkey: "packing" },
            { key: "service_deballage", tkey: "unpacking" },
            { key: "service_montage",   tkey: "assembly" },
            { key: "service_demontage", tkey: "disassembly" },
          ].map(({ key, tkey }) => (
            <button
              key={key}
              type="button"
              onClick={() => set(key, !data[key])}
              className={`flex items-center justify-center gap-2 px-3 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                data[key]
                  ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                  : "border-base-200 bg-base-200 text-base-content/50 hover:border-indigo-200 hover:text-indigo-500"
              }`}
            >
              <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                data[key] ? "border-indigo-500 bg-indigo-500" : "border-base-300"
              }`}>
                {data[key] && (
                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                )}
              </span>
              {t(`quote.${tkey}`)}
            </button>
          ))}
        </div>
      </div>

      {/* ── Notes ── */}
      <div className="bg-base-100 rounded-2xl border border-base-300 shadow-sm p-5">
        <Label>{t("quote.notes", "Commentaire / Notes")}</Label>
        <textarea
          rows={3}
          value={data.comments}
          onChange={(e) => set("comments", e.target.value)}
          className="mt-2 w-full px-3 py-2.5 rounded-xl border border-base-300 bg-base-200 text-base-content text-sm
            placeholder:text-base-content/30 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400
            focus:bg-base-100 outline-none transition-all resize-none"
          placeholder={t("quote.notesPlaceholder", "Informations utiles, accès, contraintes…")}
        />
      </div>
    </div>
  );
}
